import { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Stack,
  Chip,
  InputAdornment,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Iconify from '../../../components/iconify';
import { uploadLeaveBalances, getAllLeaveBalances } from '../../../services/leaveService';

const TEMPLATE_HEADERS = ['email', 'earnedLeaves', 'privilegedLeaves', 'month'];
const TEMPLATE_SAMPLE = [
  ['alice@company.com', 15, 5, ''],
  ['bob@company.com', 12, 3, ''],
  ['intern1@company.com', 1, 0, 1],
  ['intern2@company.com', 1, 0, 2],
  ['intern2@company.com', 1, 0, 3],
];

function downloadTemplate() {
  const rows = [TEMPLATE_HEADERS, ...TEMPLATE_SAMPLE];
  const notes = [
    [],
    ['NOTES:'],
    ['- Full-time employees: leave "month" column empty.'],
    ['- Interns: fill "month" with the month number (1 - 12). Month must not exceed the current month.'],
    ['- To upload multiple months for an intern, add one row per month.'],
  ];
  const allRows = [...rows, ...notes];
  const csv = allRows.map((r) => r.map((v) => (String(v).includes(',') ? `"${v}"` : v)).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'leave_balance_template.csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function LeaveBalanceUpload() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const fileInputRef = useRef(null);
  const searchTimerRef = useRef(null);
  const currentYear = new Date().getFullYear();

  const {
    data: balancesResponse = { data: [], total: 0 },
    isLoading: isLoadingBalances,
    refetch: refetchBalances,
  } = useQuery({
    queryKey: ['allLeaveBalances', currentYear, searchQuery, page, rowsPerPage],
    queryFn: async () => {
      try {
        const response = await getAllLeaveBalances(currentYear, {
          search: searchQuery,
          page: page + 1,
          pageSize: rowsPerPage,
        });
        return response?.data?.data || { data: [], total: 0 };
      } catch {
        return { data: [], total: 0 };
      }
    },
    keepPreviousData: true,
  });

  const balances = balancesResponse.data || [];
  const totalCount = balancesResponse.total || 0;

  // Format numeric values as floating point (e.g. 1.00); fall back to em dash when empty.
  const fmt = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));
  // Shared style so every numeric chip has the same width and its border lines up evenly.
  const numberChipSx = { minWidth: 72 };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(0);
    }, 400);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }
    try {
      setIsUploading(true);
      const response = await uploadLeaveBalances(selectedFile, currentYear);
      const result = response?.data?.data;
      setUploadResult(result);
      toast.success(response?.data?.message || 'Upload successful');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      refetchBalances();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error uploading file');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box>
      {/* Upload section */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upload Leave Balances — {currentYear}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Upload an Excel (.xlsx) or CSV file with columns: <strong>email</strong>,{' '}
          <strong>earnedLeaves</strong>, <strong>privilegedLeaves</strong>, <strong>month</strong>.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Leave <strong>month</strong> empty for full-time employees. For interns, set{' '}
          <strong>month</strong> to the month number (1–12, must not exceed the current month) — add
          one row per month to upload historical balances.
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            startIcon={<Iconify icon="eva:download-fill" />}
            onClick={downloadTemplate}
          >
            Download Template
          </Button>

          <Button
            variant="outlined"
            component="label"
            startIcon={<Iconify icon="eva:attach-fill" />}
          >
            {selectedFile ? selectedFile.name : 'Choose File'}
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              ref={fileInputRef}
            />
          </Button>

          <LoadingButton
            variant="contained"
            startIcon={<Iconify icon="eva:upload-fill" />}
            onClick={handleUpload}
            loading={isUploading}
            disabled={!selectedFile}
          >
            Upload
          </LoadingButton>
        </Stack>

        {uploadResult && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ mb: uploadResult.errors?.length ? 1 : 0 }}>
              {uploadResult.processed} record{uploadResult.processed !== 1 ? 's' : ''} updated
              successfully.
            </Alert>
            {uploadResult.errors?.length > 0 && (
              <Alert severity="warning">
                {uploadResult.errors.length} row{uploadResult.errors.length !== 1 ? 's' : ''} could
                not be processed:
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                  {uploadResult.errors.map((e, i) => (
                    <li key={i}>
                      {e.email || e.row?.email || 'Unknown'}: {e.reason}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* Current balances table */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">Current Balances — {currentYear}</Typography>
        {(totalCount > 0 || searchQuery) && (
          <TextField
            size="small"
            placeholder="Search by name or email…"
            value={searchInput}
            onChange={handleSearchChange}
            sx={{ width: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" width={18} />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Stack>

      {isLoadingBalances ? (
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      ) : totalCount === 0 && !searchQuery ? (
        <Alert severity="info">No leave balances uploaded for {currentYear} yet.</Alert>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="center">Earned Leaves</TableCell>
                  <TableCell align="center">Privileged Leaves</TableCell>
                  <TableCell align="center">Total Allocated</TableCell>
                  <TableCell align="center">Leaves Used</TableCell>
                  <TableCell align="center">Extra Leaves</TableCell>
                  <TableCell align="center">Upcoming Leaves</TableCell>
                  <TableCell align="center">Leave Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No results found for &quot;{searchQuery}&quot;
                    </TableCell>
                  </TableRow>
                ) : (
                  balances.map((row) => {
                    const isIntern = row.employmentType === 'intern';
                    const totalAllocated =
                      row.earnedLeaves !== null && row.privilegedLeaves !== null
                        ? row.earnedLeaves + row.privilegedLeaves
                        : null;
                    return (
                      <TableRow key={row.userId} hover>
                        <TableCell>{row.userName}</TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={isIntern ? 'Intern' : 'Full-time'}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: isIntern ? '#FFF3CD' : '#D0E8FF',
                              color: isIntern ? '#B76E00' : '#1877F2',
                              border: 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {isIntern ? (
                            <Chip
                              label={row.earnedLeaves !== null ? `${fmt(row.earnedLeaves)} (${row.monthsUnlocked}mo)` : '—'}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={numberChipSx}
                            />
                          ) : (
                            <Chip label={fmt(row.earnedLeaves)} size="small" color="primary" variant="outlined" sx={numberChipSx} />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={fmt(row.privilegedLeaves)}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={numberChipSx}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <strong>{fmt(totalAllocated)}</strong>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={fmt(row.leavesTaken)}
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={numberChipSx}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={row.extraLeaves > 0 ? fmt(row.extraLeaves) : '—'}
                            size="small"
                            color={row.extraLeaves > 0 ? 'error' : 'default'}
                            variant={row.extraLeaves > 0 ? 'filled' : 'outlined'}
                            sx={numberChipSx}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={row.upcomingLeaves > 0 ? fmt(row.upcomingLeaves) : '—'}
                            size="small"
                            sx={{
                              ...numberChipSx,
                              bgcolor: row.upcomingLeaves > 0 ? '#FFE4D9' : undefined,
                              color: row.upcomingLeaves > 0 ? '#C4390A' : undefined,
                            }}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={fmt(row.leaveBalance)}
                            size="small"
                            color={row.leaveBalance !== null && row.leaveBalance <= 0 ? 'error' : 'default'}
                            variant={row.leaveBalance !== null && row.leaveBalance <= 0 ? 'filled' : 'outlined'}
                            sx={numberChipSx}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}
    </Box>
  );
}
